<script setup>

import {getInitials} from "../../../../../helpers.js";
import {Link, router, usePage} from "@inertiajs/vue3";
import Avatar from "primevue/avatar";
import Button from "primevue/button";
import {computed, ref} from "vue";
import DatePicker from "primevue/datepicker";
import Popover from "primevue/popover";
import moment from "moment";
import { trans } from 'laravel-vue-i18n';

const props = defineProps({
    reservation: Object,
    changeable: {
        type: Boolean,
        default: true
    }
})

const fmtPrice = (val) => new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(val)

const page = usePage();

const modalStyle = computed(() => {
    return `background-image: url(${props.reservation.reservation_forms?.theme?.background_image_url})`;
})

const showActivityLog = ref(false);

const emit = defineEmits(["change-reservation", 'close']);

const notifyPopover = ref();
const notifyOptions = [
    { type: 'new_client',      label: trans('reservations.notify_new_client') },
    { type: 'confirm_client',  label: trans('reservations.notify_confirm') },
    { type: 'cancel_client',   label: trans('reservations.notify_cancel') },
    { type: 'reminder_client', label: trans('reservations.notify_reminder') },
];
const sendNotification = (type) => {
    router.post(`/app/reservations/${props.reservation.id}/notify`, { type }, {
        preserveState: true,
        onSuccess: () => notifyPopover.value.hide(),
    });
};

function acceptReservation(reservation) {
    reservation.state = 'accepted';
    router.get(`/app/forms/event/${reservation.id}/accept`, {}, {
        only: ['reservations'],
        preserveState: true,
        onFinish: () => {
            emit('close');
        }
    });
}

function rejectReservation(reservation) {
    reservation.state = 'rejected';
    router.get(`/app/forms/event/${reservation.id}/reject`, {}, {
        only: ['reservations'],
        preserveState: true,
        onFinish: () => {
            emit('close');
        }
    });
}

const formData = computed(() => {
    const fd = JSON.parse(props.reservation.form_data);

    if (Array.isArray(fd) || !fd) {
        return false;
    }

    const data = Object.entries(fd);

    let content = '';

    data.forEach((e) => {
       content += `<b>${e[0]}</b>: ${e[1]}<br>`;
    });
    return content;
})

const eventChanger = ref();

const changeEvent = ($event, reservation) => {
    eventChanger.value.toggle($event);
}
const dateComputed = computed({
    get: () => new Date(props.reservation.date || '2025-01-01'),
    set: (val) => {
        props.reservation.date = moment(val).format('YYYY-MM-DD');
        props.reservation.formatted_date = moment(val).format('D.MM.yy');
    }
});

const timeStartComputed = computed({
    get: () => {
        const [hours, minutes] = (props.reservation.time_start || '00:00').split(':');
        const date = new Date('2025-01-01T00:00');
        date.setHours(hours, minutes);
        return date;
    },
    set: (val) => {
        const hours = String(val.getHours()).padStart(2, '0');
        const minutes = String(val.getMinutes()).padStart(2, '0');
        props.reservation.time_start = `${hours}:${minutes}:00`;

        // Pokud je start větší nebo rovno end, posuneme end o hodinu nahoru
        const endDate = timeEndComputed.value;
        if (val >= endDate) {
            endDate.setHours(val.getHours() + 1);
            props.reservation.time_end = `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}:00`;
        }
    }
});

const timeEndComputed = computed({
    get: () => {
        const [hours, minutes] = (props.reservation.time_end || '00:00').split(':');
        const date = new Date('2025-01-01T00:00');
        date.setHours(hours, minutes);
        return date;
    },
    set: (val) => {
        const hours = String(val.getHours()).padStart(2, '0');
        const minutes = String(val.getMinutes()).padStart(2, '0');
        props.reservation.time_end = `${hours}:${minutes}:00`;
    }
});

const getActivityLog = (rawLog, reservation) => {
    const logMap = {
        new_reservation:                          { origin: 'client',    label: trans('reservations.activity_new'),             category: 'actions' },
        confirm_reservation:                      { origin: 'user',      label: trans('reservations.activity_confirm'),          category: 'actions' },
        cancel_reservation:                       { origin: 'ambiguous', label: trans('reservations.activity_cancel'),           category: 'actions' },
        change_reservation_email:                 { origin: 'system',    label: trans('reservations.activity_email_change'),     category: 'notification' },
        confirm_reservation_email:                { origin: 'system',    label: trans('reservations.activity_email_confirm'),    category: 'notification' },
        change_reservation_sms:                   { origin: 'system',    label: trans('reservations.activity_sms_change'),       category: 'notification' },
        confirm_reservation_sms:                  { origin: 'system',    label: trans('reservations.activity_sms_confirm'),      category: 'notification' },
        pending_state_changed:                    { origin: 'user',      label: trans('reservations.activity_state_pending'),    category: 'actions' },
        accepted_state_changed:                   { origin: 'user',      label: trans('reservations.activity_state_accepted'),   category: 'actions' },
        rejected_state_changed:                   { origin: 'user',      label: trans('reservations.activity_state_rejected'),   category: 'actions' },
        new_reservation_email:                    { origin: 'system',    label: trans('reservations.activity_email_new'),        category: 'notifications' },
        cancel_reservation_email:                 { origin: 'system',    label: trans('reservations.activity_email_cancel'),     category: 'notifications' },
        new_reservation_sms:                      { origin: 'system',    label: trans('reservations.activity_sms_new'),          category: 'notifications' },
        cancel_reservation_sms:                   { origin: 'system',    label: trans('reservations.activity_sms_cancel'),       category: 'notifications' },
        change_of_date:                           { origin: 'ambiguous', label: trans('reservations.activity_date_change'),       category: 'actions' },
        change_of_date_of_time_start_of_time_end: { origin: 'ambiguous', label: trans('reservations.activity_datetime_change'),  category: 'actions' },
        change_of_time_start_of_time_end:         { origin: 'ambiguous', label: trans('reservations.activity_time_change'),      category: 'actions' },
        change_of_time_start:                     { origin: 'ambiguous', label: trans('reservations.activity_time_start_change'), category: 'actions' },
        change_of_time_end:                       { origin: 'ambiguous', label: trans('reservations.activity_time_end_change'),  category: 'actions' },
    }

    const originMap = {
        ambiguous: {
            name: page.props.user?.name + ' ' + trans('common.or') + ' ' + reservation.name
        },
        system: {
            name: trans('common.app_roori')
        },
        user: {
            name: page.props.user?.name
        },
        client: {
            name: reservation.name
        }
    }

    const logData = logMap[rawLog.action];

    return {
        origin: originMap[logData?.origin],
        label: logData.label,
        message: logData.message,
        date: rawLog.date_friendly,
    }
}
</script>

<template>
    <div class="bg-blue-100 dark:bg-neutral-900" style="background-size: cover; background-position: center"
         :style="modalStyle">
        <div class=" h-full p-5 flex items-end gap-3" :class="reservation.reservation_forms?.theme?.background_image_url !== '' ? 'bg-gradient-to-t from-black/30 dark:from-black/70' : ''">
            <Avatar :label="getInitials(reservation.name)" shape="circle"
                    class="!bg-blue-600 !text-white "></Avatar>
            <p class="font-semibold text-white mb-1 grow" :class="reservation.state === 'rejected' ? 'text-decoration-line-through':''">
                <span>[</span>{{ reservation.name }}<span>]</span>
            </p>
            <slot name="buttons"></slot>
            <Link :href="`/app/reservations/${reservation.sqid}`">
                <Button size="small" rounded severity="secondary" icon="pi pi-external-link"></Button>
            </Link>
            <Button size="small" rounded severity="secondary"
                    icon="pi pi-send" :label="$t('common.send')"
                    @click="notifyPopover.toggle($event)"
            ></Button>
            <Popover ref="notifyPopover">
                <div class="flex flex-col gap-1 w-44">
                    <p class="text-xs text-gray-400 px-1 pb-1">{{ $t('reservations.notify_type') }}</p>
                    <Button v-for="n in notifyOptions" :key="n.type" :label="n.label"
                            size="small" text severity="secondary" class="justify-start"
                            @click="sendNotification(n.type)" />
                </div>
            </Popover>
            <Button v-if="reservation.activity_logs.length" size="small" rounded severity="secondary"
                    :icon="showActivityLog ? 'pi pi-arrow-left' : 'pi pi-info-circle'"
                    :label="showActivityLog ? $t('common.back') : $t('reservations.section_activity')"
                    @click="showActivityLog = !showActivityLog"
            ></Button>
        </div>
    </div>
    <div v-if="showActivityLog" class="p-4 flex flex-col gap-4 h-[288px] overflow-y-auto bg-neutral-50">
        <div v-for="log in reservation.activity_logs?.map(el => getActivityLog(el, reservation))" class="flex gap-2">
            <Avatar :label="getInitials(log.origin?.name)" shape="circle"
                    class="!min-w-[24px] !min-h-[24px] !max-w-[24px] !max-h-[24px] !text-xs"></Avatar>
            <div class="text-sm">
                <p>
                    <b>{{log.origin?.name}}</b> {{log.label}}
                </p>
                <p class="opacity-60">
                    {{log.date}}
                </p>
            </div>
        </div>
    </div>
    <template v-else>
        <div class="px-6 py-2 text-gray-700 border-t border-b dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 dark:text-white flex items-center gap-2">
            <p>{{ $t('reservations.confirm_question') }}</p>
            <div class="grow"></div>
            <Button :label="$t('common.yes')" @click="acceptReservation(reservation)" class="text-xs" rounded size="small" :severity="reservation.state === 'accepted' ? 'success' : 'secondary'"></Button>
            <Button :label="$t('common.no')" @click="rejectReservation(reservation)" class="text-xs" rounded size="small" :severity="reservation.state === 'rejected' ? 'danger' : 'secondary'"></Button>
        </div>
        <div class="overflow-y-scroll flex flex-row-reverse max-md:flex-col-reverse gap-3 max-md:flex-wrap p-5">
            <div v-if="formData || reservation.services?.name" class="flex flex-col gap-3 md:w-1/2">
                <div v-if="reservation.services?.name" class="flex">
                    <i class="pi pi-briefcase pl-1.5 pr-4 text-gray-500 !text-xl"></i>
                    <div class="border dark:border-neutral-700 w-full rounded-lg p-1 px-2.5">
                        <p class="text-xs text-gray-400 -mb-1">
                            {{ $t('reservations.field_service') }}
                            <span v-if="reservation.workers?.name">
                            {{ $t('reservations.field_colleague') }}
                        </span>
                        </p>
                        <p>
                            {{ reservation.services?.name }}
                            <span v-if="reservation.workers?.name">
                            - {{reservation.workers?.name}}
                        </span>
                        </p>
                    </div>
                </div>
                <div class="flex grow">
                    <i class="pi pi-bars pl-1.5 pr-4 text-gray-500 !text-xl"></i>
                    <div class="border dark:border-neutral-700 w-full rounded-lg p-1 px-2.5">
                        <p class="text-xs text-gray-400 ">
                            {{ $t('reservations.meta_form') }}
                        </p>
                        <p v-if="formData" v-html="formData"></p>
                        <p v-else class="text-sm italic opacity-70 mt-1">
                            {{ $t('reservations.field_no_extra') }}
                        </p>
                    </div>
                </div>
            </div>
            <div class="flex flex-col gap-3 grow">
                <div class="flex">
                    <i class="pi pi-clock pl-1.5 pr-4 text-gray-500 !text-xl"></i>
                    <div class="border dark:border-neutral-700 w-full rounded-lg p-1 pl-2.5 flex justify-between align-center">
                        <div>
                            <p class="text-xs text-gray-400 -mb-1">{{ $t('reservations.field_date_label') }}</p>
                            <p v-if="reservation.time_start">
                                {{ reservation.formatted_date }}
                                <span class="bg-neutral-200 dark:bg-neutral-700 py-0.5 px-1.5 rounded-lg text-sm">{{ reservation.time_start.substring(0, 5) }}</span>
                                -<span class="bg-neutral-200 dark:bg-neutral-700 py-0.5 px-1.5 rounded-lg text-sm">{{ reservation.time_end.substring(0, 5) }}</span>
                            </p>
                            <p v-else>
                                <span class="bg-neutral-200 dark:bg-neutral-700 py-0.5 px-1.5 rounded-lg text-sm">{{ reservation.formatted_date }}</span>
                                –<span class="bg-neutral-200 dark:bg-neutral-700 py-0.5 px-1.5 rounded-lg text-sm">{{ reservation.formatted_date_end }}</span>
                            </p>
                        </div>
                        <Button v-if="changeable" size="small" text icon="pi pi-pencil" severity="secondary" @click="changeEvent($event, reservation)"></Button>
                        <Popover ref="eventChanger">
                            <div class="flex justify-center items-center gap-3">
                                <DatePicker v-model="dateComputed" class="w-[124px]" date-format="dd.mm.yy" fluid />
                                <DatePicker time-only v-model="timeStartComputed" class="w-[72px]" fluid variant="filled"></DatePicker>
                                <span>{{ $t('reservations.time_until') }}</span>
                                <DatePicker time-only v-model="timeEndComputed" :min-date="timeStartComputed" class="w-[72px]" fluid variant="filled"></DatePicker>
                                <Button icon="pi pi-check" :label="$t('common.change')" @click="() => {
                                eventChanger.hide();
                                emit('change-reservation', moment(dateComputed).format('YYYY-MM-DD'), props.reservation.time_start, props.reservation.time_end);
                            }" variant="text"></Button>
                            </div>
                        </Popover>
                    </div>
                </div>
                <div class="flex">
                    <i class="pi pi-user pl-1.5 pr-4 text-gray-500 !text-xl"></i>
                    <div class="border dark:border-neutral-700 w-full rounded-lg p-1 px-2.5">
                        <p class="text-xs text-gray-400 -mb-1">{{ $t('reservations.field_name') }}</p>
                        <p>{{ reservation.name }}</p>
                    </div>
                </div>
                <div class="flex">
                    <i class="pi pi-envelope pl-1.5 pr-4 text-gray-500 !text-xl"></i>
                    <div class="border dark:border-neutral-700 w-full rounded-lg p-1 px-2.5">
                        <p class="text-xs text-gray-400 -mb-1">{{ $t('reservations.field_email') }}</p>
                        <p>{{ reservation.email }}</p>
                    </div>
                </div>
                <div v-if="reservation.phone" class="flex">
                    <i class="pi pi-phone pl-1.5 pr-4 text-gray-500 !text-xl"></i>
                    <div class="border dark:border-neutral-700 w-full rounded-lg p-1 px-2.5">
                        <p class="text-xs text-gray-400 -mb-1">{{ $t('reservations.field_phone') }}</p>
                        <p>{{ reservation.phone }}</p>
                    </div>
                </div>
                <div v-if="reservation.message" class="flex">
                    <i class="pi pi-align-left pl-1.5 pr-4 text-gray-500 !text-xl"></i>
                    <div class="border dark:border-neutral-700 w-full rounded-lg p-1 px-2.5">
                        <p class="text-xs text-gray-400 -mb-1">{{ $t('reservations.meta_message') }}</p>
                        <p>{{ reservation.message }}</p>
                    </div>
                </div>
                <div v-if="reservation.adults != null" class="flex">
                    <i class="pi pi-users pl-1.5 pr-4 text-gray-500 !text-xl"></i>
                    <div class="border dark:border-neutral-700 w-full rounded-lg p-1 px-2.5">
                        <p class="text-xs text-gray-400 -mb-1">{{ $t('reservations.meta_guests') }}</p>
                        <p>
                            {{ reservation.adults }} {{ $t('common.adults') }}
                            <template v-if="reservation.children"> · {{ reservation.children }} {{ $t('common.children') }}</template>
                            <template v-if="reservation.pets"> · {{ reservation.pets }} {{ $t('common.pets') }}</template>
                        </p>
                    </div>
                </div>
                <div v-if="reservation.rental_items" class="flex">
                    <i class="pi pi-home pl-1.5 pr-4 text-gray-500 !text-xl"></i>
                    <div class="border dark:border-neutral-700 w-full rounded-lg p-1 px-2.5">
                        <p class="text-xs text-gray-400 -mb-1">{{ $t('reservations.field_rental_item') }}</p>
                        <p>{{ reservation.rental_items.name }}</p>
                    </div>
                </div>
                <div v-if="reservation.bill_items?.length" class="flex">
                    <i class="pi pi-receipt pl-1.5 pr-4 text-gray-500 !text-xl"></i>
                    <div class="border dark:border-neutral-700 w-full rounded-lg p-1 px-2.5">
                        <p class="text-xs text-gray-400 mb-1">{{ $t('reservations.field_bill_items') }}</p>
                        <div v-for="item in reservation.bill_items" :key="item.id"
                             class="flex justify-between text-sm py-0.5">
                            <span>{{ item.description }}<span v-if="item.amount > 1"> × {{ item.amount }}</span></span>
                            <span :class="item.unit_price_with_vat * item.amount < 0 ? 'text-green-600 dark:text-green-400' : ''">
                                {{ fmtPrice(item.unit_price_with_vat * item.amount) }}
                            </span>
                        </div>
                        <div class="border-t dark:border-neutral-700 mt-1 pt-1 flex justify-between font-semibold text-sm">
                            <span>{{ $t('common.total') }}</span>
                            <span>{{ fmtPrice(reservation.bill_items.reduce((s, i) => s + i.unit_price_with_vat * i.amount, 0)) }}</span>
                        </div>
                    </div>
                </div>
                <div class="flex">
                    <i class="pi pi-calendar pl-1.5 pr-4 text-gray-500 !text-xl"></i>
                    <div class="border dark:border-neutral-700 w-full rounded-lg bg-gray-50 dark:bg-gray-950 p-1 px-2.5 mr-3">
                        <p class="text-xs text-gray-400 -mb-1">{{ $t('reservations.field_booking_form') }}</p>
                        <p>{{ reservation.reservation_forms.name }}</p>
                    </div>
                    <Link :href="`/app/forms/${reservation.reservation_forms.slug}`" class="border dark:border-neutral-700 rounded-lg overflow-hidden w-14">
                        <Button icon="pi pi-eye" class="!w-10 h-full" severity="secondary"></Button>
                    </Link>
                </div>
            </div>
        </div>
    </template>
</template>

<style scoped lang="scss">
</style>
